import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token") || "";
    if (!t) {
      setError("This reset link is missing its token. Please request a new one.");
      return;
    }
    setToken(t);
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/password/reset`, {
        token,
        new_password: password,
      });
      localStorage.setItem("session_token", res.data.session_token);
      setDone(true);
      // Small delay so the user sees confirmation before we redirect.
      setTimeout(() => { window.location.href = "/dashboard"; }, 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8" data-testid="reset-password-card">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-[#0D9488] rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 font-['Manrope']">Reset your password</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">Choose a new password to sign back in.</p>

        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="reset-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-slate-700 font-medium">Password updated — signing you in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {error && (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="reset-error">
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <label htmlFor="new-password" className="flex items-center justify-between text-xs font-medium text-slate-600">
                <span>New password (min 8 characters)</span>
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                  data-testid="reset-show-password"
                >
                  {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {show ? "Hide" : "Show"}
                </button>
              </label>
              <Input
                id="new-password"
                type={show ? "text" : "password"}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting || !token}
                data-testid="reset-password-input"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="confirm-password" className="block text-xs font-medium text-slate-600">Confirm new password</label>
              <Input
                id="confirm-password"
                type={show ? "text" : "password"}
                placeholder="Re-enter password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting || !token}
                data-testid="reset-confirm-input"
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !token || !password || !confirm}
              data-testid="reset-submit-btn"
              className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold py-3 h-auto"
            >
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
              data-testid="reset-back-btn"
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
